/**
 * The preview (1.I-2 deliverable 28): a peek's first paint before its editor exists, drawn from
 * `docs.get`'s answer alone. A region "Preview: <title>" (`nav.peek.preview`, `aria-busy`), laid
 * out as `DocEditor` lays out the page (the status line, the title, the body), so the editor that
 * replaces it puts its first block where the preview's was. It is not editable, holds nothing
 * tabbable and handles no key. Its code is free of ProseMirror, so it sits in the root chunk with
 * the editor's stylesheet; it marks `codex.peek.preview` once it has painted.
 */
import '@codex/editor/styles/editor.css';
import type { DocumentV1 } from '@codex/contracts';
import { Pill } from '@codex/ui';
import { createElement, type ReactNode, useEffect, useMemo, useRef } from 'react';
import { afterNextPaint, markPeek } from './peek-trace.js';
import { type PreviewNode, renderPreviewBody } from './preview-render.js';

/** The status line's words while the preview shows (the editor's own line replaces it). */
export const PREVIEW_STATUS = 'Opening…';

/** The renderer's attribute names React spells otherwise. */
const REACT_NAMES: Readonly<Record<string, string>> = {
  class: 'className',
  colspan: 'colSpan',
  rowspan: 'rowSpan',
};

function toElement(node: PreviewNode, key: number): ReactNode {
  if (typeof node === 'string') return node;
  const props: Record<string, unknown> = { key };
  for (const [name, value] of Object.entries(node.attrs)) props[REACT_NAMES[name] ?? name] = value;
  return createElement(node.tag, props, ...node.children.map(toElement));
}

export interface PeekPreviewProps {
  readonly doc: DocumentV1;
  /** Told once the preview has painted. */
  readonly onPainted?: (() => void) | undefined;
}

export function PeekPreview({ doc, onPainted }: PeekPreviewProps) {
  const blocks = useMemo(() => renderPreviewBody(doc.body).map(toElement), [doc.body]);
  const title = doc.titlePending ? '' : doc.title.trim();
  const painted = useRef(onPainted);
  painted.current = onPainted;
  useEffect(
    () =>
      afterNextPaint(() => {
        markPeek('preview');
        painted.current?.();
      }),
    [],
  );
  return (
    <section
      className="cx-nav-preview"
      aria-label={`Preview: ${title === '' ? 'Untitled' : title}`}
      aria-busy="true"
      data-testid="nav.peek.preview"
    >
      <div className="cx-nav-preview__doc">
        <div aria-hidden="true">
          <div>
            <Pill tone="neutral">{PREVIEW_STATUS}</Pill>
          </div>
        </div>
        <div className="cx-editor">
          <h1
            className={title === '' ? 'cx-editor__title cx-nav-preview__ghost' : 'cx-editor__title'}
          >
            {title === '' ? 'Untitled' : title}
          </h1>
          <div className="cx-editor__content">
            <div className="cx-editor__body">{blocks}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
